'use client';

import { AuthGuardComponent } from '@/components/auth/auth-guard';
import { CinemarkLink } from '@/components/cinemark-link/cinemark-link';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs';
import { AnalyticsPageCategories } from '@/enums/analytics-type';
import { useIsMobile } from '@/hooks/is-mobile';
import { useProfileTabsData } from '@/hooks/profile/profile-tabs';
import { dataTestTransformer } from '@/utils/data-test-helper';
import { clsx } from 'clsx';
import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';

type AccountNavigationRoute = {
  id: string;
  pageTitle: string;
  route: string;
  tabTitle: string;
  useAnchor: boolean;
};

const accountNavigationRoutes: AccountNavigationRoute[] = [
  { id: 'profile-link', pageTitle: 'Profile & Payment', route: '/account/profile', tabTitle: 'Your Profile', useAnchor: false },
  { id: 'purchases-link', pageTitle: 'Tickets & Purchases', route: '/account/purchases', tabTitle: 'Purchases', useAnchor: true },
  { id: 'subscriptions-link', pageTitle: 'Email Subscriptions', route: '/account/subscriptions', tabTitle: 'Email Subscriptions', useAnchor: false },
  { id: 'gift-cards-link', pageTitle: 'Your Gift Cards', route: '/account/giftcards', tabTitle: 'Your Gift Cards', useAnchor: true },
];

const cinemarkCashRoute: AccountNavigationRoute = {
  id: 'cinemark-cash-link',
  pageTitle: 'Cinemark Cash',
  route: '/account/cinemarkcash',
  tabTitle: 'Your Cinemark Cash',
  useAnchor: false,
};

const watchlistRoute: AccountNavigationRoute = {
  id: 'watchlist-link',
  pageTitle: 'Watch List',
  route: '/account/watchlist',
  tabTitle: 'Your Watchlist',
  useAnchor: false,
};

type ProfileLayoutProps = {
  children: ReactNode;
};

const ProfileLayout = ({ children }: ProfileLayoutProps): React.JSX.Element => {
  const pathname = usePathname();
  const [currentTab, setCurrentTab] = useState<AccountNavigationRoute>();
  const [navigationRoutes, setNavigationRoutes] = useState<AccountNavigationRoute[]>(accountNavigationRoutes);
  const { hasCinemarkCash, hasWatchlist, isLoading } = useProfileTabsData();
  const { isMobile } = useIsMobile();

  useEffect(() => {
    if (!isLoading) {
      const updatedRoutes = [...navigationRoutes];
      const cinemarkCashRouteExists = updatedRoutes.some(r => r.route === cinemarkCashRoute.route);
      const watchlistRouteExists = updatedRoutes.some(r => r.route === watchlistRoute.route);

      if (hasWatchlist && !watchlistRouteExists) {
        updatedRoutes.push(watchlistRoute);
      } else if (!hasWatchlist && watchlistRouteExists) {
        const watchlistIndex = updatedRoutes.findIndex(r => r.route === watchlistRoute.route);
        updatedRoutes.splice(watchlistIndex, 1);
      }

      if (hasCinemarkCash && !cinemarkCashRouteExists) {
        updatedRoutes.push(cinemarkCashRoute);
      } else if (!hasCinemarkCash && cinemarkCashRouteExists) {
        const cinemarkCashIndex = updatedRoutes.findIndex(r => r.route === cinemarkCashRoute.route);
        updatedRoutes.splice(cinemarkCashIndex, 1);
      }

      setNavigationRoutes(updatedRoutes);
    }
  // We don't want to add navigationRoutes as a dependency here because it will cause an infinite loop
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasCinemarkCash, hasWatchlist, isLoading]);

  useEffect(() => {
    setCurrentTab(navigationRoutes.find(r => pathname?.startsWith(r.route)));
  }, [navigationRoutes, pathname]);

  return (
    <AuthGuardComponent>
      <Tabs className="container-lg"
        value={currentTab?.id}
      >
        {
          !isMobile &&
          <div className={clsx('flex items-center justify-between flex-wrap gap-4', !currentTab && 'hidden')}>
            <h1 className="flex-auto font-bold"
              data-test="page-title"
            >
              {currentTab?.pageTitle}
            </h1>

            <ScrollArea className="self-end max-w-full max-md:hidden">
              <TabsList className="mb-2">
                {navigationRoutes.map(route =>
                  <TabsTrigger key={route.id}
                    asChild
                    // FIXME: Add text-base class to TabsTrigger component in tabs.tsx (PBI-133647)
                    className="text-base"
                    value={route.id}
                  >
                    <CinemarkLink dataLinkCategory={AnalyticsPageCategories.PROFILE}
                      dataLinkName={route.tabTitle}
                      dataTest={`tab-trigger-${dataTestTransformer(route.tabTitle)}`}
                      href={route.route}
                      useAnchor={route.useAnchor}
                    >
                      {route.tabTitle}
                    </CinemarkLink>
                  </TabsTrigger>
                )}
              </TabsList>

              <ScrollBar orientation="horizontal"/>
            </ScrollArea>
          </div>
        }

        {children}
      </Tabs>
    </AuthGuardComponent>
  );
};

export default ProfileLayout;
